import { useEffect, useState } from "react";
import { useScrollLock } from "../../hooks/useScrollLock";
import { useNavigate } from "react-router-dom";

import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ModalText,
  Row,
  OptionCard,
  OptionsWrapper,
  ExpandedContent,
  InputLabel,
  ModelNameInput,
  ChevronBack,
  OptionTitle,
  OptionDescription,
  OptionRequires,
  Divider,
  ChevronRight,
  StyledColumn,
  ModalTextRow,
} from "./TrainVipModal.styles";

import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as ImageSvg } from "../../assets/icons/image.svg";
import { ReactComponent as VideoSvg } from "../../assets/icons/video.svg";
// import { ReactComponent as PersonSvg } from "../../assets/icons/person.svg";

import Button from "../utils/Button/Button";

const OPTIONS = [
  {
    id: "video",
    icon: <VideoSvg />,
    title: "Image to Video",
    description: "Turn a still image into a realistic motion video.",
    requires:
      "Requires: 10-20 videos showing transition cuts from still picture to motion video.",
    disabled: false,
    opacity: true,
  },
  {
    id: "image",
    icon: <ImageSvg />,
    title: "Image to Image",
    description: "Transform your image into a new styled version.",
    requires: "Requires: 15-50 Before & after examples of images.",
    disabled: false,
    opacity: false,
  },
  // {
  //   id: "person",
  //   icon: <PersonSvg />,
  //   title: "Train Person",
  //   description: "Create a custom AI model of one person.",
  //   requires:
  //     "Requires: 20-40 high quality photos of the same person from different angles",
  //   disabled: true,
  //   opacity: true,
  // },
];

const TrainVipModal = ({ isOpen, onClose, onContinue, loading = false }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [modelName, setModelName] = useState("");

  const navigate = useNavigate();
  useScrollLock(shouldRender);

  useEffect(() => {
    if (!isOpen) {
      setSelectedOption(null);
      setModelName("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsAnimating(false);
      const timer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      return () => cancelAnimationFrame(timer);
    } else if (shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  if (!shouldRender) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSelectOption = (option) => {
    if (option.disabled) return;
    const randomId = Math.floor(1000 + Math.random() * 9000);
    setModelName(`New Template #${randomId}`);
    setSelectedOption(option);
  };

  const handleBack = () => {
    setSelectedOption(null);
    setModelName("");
  };

  const handleContinue = () => {
    if (modelName.trim() && !loading) {
      onContinue(modelName.trim(), selectedOption?.id);
    }
  };

  const handleTutorialClick = () => {
    navigate("/train-tutorial");
    onClose();
  };

  return (
    <ModalOverlay onClick={handleOverlayClick} $isAnimating={isAnimating}>
      <ModalContainer $isAnimating={isAnimating} $expanded={!!selectedOption}>
        <ModalContent>
          {selectedOption ? (
            <Row style={{ justifyContent: "space-between" }}>
              <ChevronBack onClick={handleBack} $visible={!!selectedOption}>
                <ChevronSvg />
              </ChevronBack>

              <ModalTextRow style={{ width: "unset" }}>
                {selectedOption.icon}
                <ModalTitle>{selectedOption.title}</ModalTitle>
              </ModalTextRow>

              <CloseButton onClick={onClose}>
                <CloseSvg />
              </CloseButton>
            </Row>
          ) : (
            <Row>
              <ModalTitle>Train</ModalTitle>

              <CloseButton onClick={onClose}>
                <CloseSvg />
              </CloseButton>
            </Row>
          )}

          {/* OPTIONS LIST */}
          <OptionsWrapper $collapsed={!!selectedOption}>
            <div>
              {OPTIONS.map((option) => (
                <OptionCard
                  key={option.id}
                  $disabled={option.disabled}
                  $hiding={!!selectedOption && selectedOption.id !== option.id}
                  $selected={selectedOption?.id === option.id}
                  onClick={() => handleSelectOption(option)}
                >
                  <StyledColumn>
                    <ModalTextRow $disabled={option.disabled}>
                      {option.icon}
                      <OptionTitle $disabled={option.disabled}>
                        {option.title}
                      </OptionTitle>
                    </ModalTextRow>
                    <OptionDescription $disabled={option.disabled}>
                      {option.description}
                    </OptionDescription>
                    <OptionRequires>{option.requires}</OptionRequires>
                    {option.disabled && (
                      <OptionRequires
                        style={{ color: "#555", fontStyle: "italic" }}
                      >
                        Coming soon
                      </OptionRequires>
                    )}
                  </StyledColumn>
                  <ChevronRight>
                    <ChevronSvg />
                  </ChevronRight>
                </OptionCard>
              ))}
            </div>
          </OptionsWrapper>

          {/* EXPANDED FORM */}
          <ExpandedContent $visible={!!selectedOption}>
            <div>
              {selectedOption?.id === "image" ? (
                <ModalText>
                  Upload paired before-and-after images that show the exact
                  change you want the model to learn. We train a custom template
                  from those examples, so it can reproduce the same
                  transformation on any other image. <br />
                  <br />
                  Once trained, your template can apply that same transformation
                  to new images - for example, changing an outfit, material,
                  color, or styling while preserving the original subject.
                  <br />
                  <br />
                  This process creates an image transformation template based on
                  your examples, allowing you to apply the same action to new
                  images.
                </ModalText>
              ) : (
                <ModalText>
                  Upload similar video clips showing the exact action you want
                  to capture, and we'll train a custom template based on them.
                  Once trained, your template lets you apply that same action to
                  any other image - bringing the motion to life in new videos.
                  <br />
                  <br />
                  This process creates a private action template that captures
                  motion (not people) from your clips and applies the motion to
                  any image you upload.
                </ModalText>
              )}

              <Divider />

              <ModalText $bold>Creator Rewards</ModalText>

              <ModalText>
                • Earn 3 credits per use (first 30 days)
                <br />• Then 2 credits per use (after 30 days)
                <br />• Then 1 credit per use (after 90 days)
              </ModalText>

              <ModalText>
                Earned credits can be withdrawn in USDT or used on the platform.
              </ModalText>

              <ModalText $italic>
                VIP Creators receive higher earnings. Once you have 10+
                high-quality templates live, you will be eligible to apply to
                join VIP.
              </ModalText>

              {selectedOption?.id === "video" && (
                <ModalText onClick={handleTutorialClick} $click>
                  📚 View our training tutorial to get started
                </ModalText>
              )}

              <Divider />

              <InputLabel>Template Name</InputLabel>
              <ModelNameInput
                type="text"
                placeholder="Enter template name..."
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                maxLength={50}
              />

              <Button
                onClick={handleContinue}
                disabled={!modelName.trim() || loading}
                loading={loading}
                title={"Continue"}
                style={{ width: 180, alignSelf: "center", marginTop: 4 }}
              />
            </div>
          </ExpandedContent>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TrainVipModal;
